import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import axios from 'axios'
import { IconPaths, KTCard, KTCardBody } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { Button, CustomTabs, ListLoading, TabItem, TableErrorState } from 'app/components'
import { getDispositivoGestao } from '../core/_requests'
import { useRecarregaAoFecharModal } from '../core/useRecarregaAoFecharModal'
import { rotuloDispositivo, rotuloMaleta } from '../core/rotulos'
import { SituacaoSelo } from '../components/Selos'
import { useEditarDispositivo } from '../components/modal/useEditarDispositivo'
import { DadosAba } from './abas/DadosAba'
import { MaletasAba } from './abas/MaletasAba'
import { AfericoesAba } from './abas/AfericoesAba'

type Aba = 'dados' | 'maletas' | 'afericoes'
const ABAS: Aba[] = ['dados', 'maletas', 'afericoes']

export const DispositivoDetalhe = () => {
  const { t } = useT()
  const { id } = useParams()
  const idDispositivo = Number(id)
  const [busca, setBusca] = useSearchParams()
  const abaDaUrl = busca.get('aba') as Aba | null
  const aba: Aba = abaDaUrl && ABAS.includes(abaDaUrl) ? abaDaUrl : 'dados'
  const editar = useEditarDispositivo()

  const {
    data: dispositivo,
    isLoading,
    error,
    refetch,
  } = useQuery(['dispositivo-gestao', idDispositivo], () => getDispositivoGestao(idDispositivo), {
    enabled: Number.isFinite(idDispositivo),
    refetchOnWindowFocus: false,
    retry: false,
  })
  useRecarregaAoFecharModal(refetch)

  const abas: TabItem<Aba>[] = [
    { id: 'dados', label: t('pagina.dispositivos.aba.dados') },
    { id: 'maletas', label: t('pagina.dispositivos.aba.maletas') },
    { id: 'afericoes', label: t('pagina.dispositivos.aba.afericoes') },
  ]

  const voltar = (
    <Link to='/apps/admin/dispositivos' className='fw-semibold'>
      {t('pagina.dispositivos.detalhe.voltar')}
    </Link>
  )

  if (isLoading && !dispositivo) return <ListLoading />

  if (error || !dispositivo) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    return (
      <KTCard>
        <KTCardBody>
          <TableErrorState
            title={t(
              status === 404
                ? 'pagina.dispositivos.detalhe.nao_encontrado'
                : status === 403
                ? 'pagina.dispositivos.detalhe.sem_permissao'
                : 'pagina.dispositivos.detalhe.erro'
            )}
            description={voltar}
            onRetry={status === 404 || status === 403 ? undefined : () => refetch()}
          />
        </KTCardBody>
      </KTCard>
    )
  }

  const nome = rotuloDispositivo(t, dispositivo)

  // O modal edita o mesmo cadastro que a página acabou de carregar: reaproveita, sem novo GET.
  const abrirEdicao = () => editar(nome, () => Promise.resolve(dispositivo))

  const maleta = dispositivo.maletaAtual

  return (
    <KTCard>
      <KTCardBody>
        <div className='mb-4'>{voltar}</div>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-4 mb-6'>
          <div>
            <h2 className='fs-3 fw-bold text-gray-900 mb-2'>{nome}</h2>
            <div className='d-flex flex-wrap align-items-center gap-3 text-gray-700'>
              <SituacaoSelo situacao={dispositivo.situacao} />
              <span>
                {t('pagina.dispositivos.detalhe.empresa', {
                  empresa: dispositivo.empresa?.nome ?? '—',
                })}
              </span>
              <span>
                {maleta ? (
                  <Link to={`/apps/admin/telekits/${maleta.idTelekit}`}>
                    {rotuloMaleta(t, maleta)}
                  </Link>
                ) : (
                  t('pagina.dispositivos.detalhe.sem_maleta')
                )}
              </span>
            </div>
          </div>
          <Button className='btn-primary' icon={IconPaths.Pen} onClick={abrirEdicao}>
            {t('common.edit')}
          </Button>
        </div>

        <CustomTabs<Aba>
          tabs={abas}
          activeTab={aba}
          onChange={(nova) => setBusca({ aba: nova })}
          className='mb-6'
          tabClassName='fs-5 fw-semibold text-gray-700'
          activeTabClassName='text-gray-900 fw-bolder'
        />

        {aba === 'dados' && <DadosAba dispositivo={dispositivo} />}
        {aba === 'maletas' && <MaletasAba idDispositivo={idDispositivo} />}
        {aba === 'afericoes' && <AfericoesAba idDispositivo={idDispositivo} />}
      </KTCardBody>
    </KTCard>
  )
}
